import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from 'oidc-client-ts';
import { getOidcUserManager } from '../../services/oidcClient';
import { apiClient } from '../../services/apiClient';
import {
  getAccessToken as getEmailAccessToken,
  getStoredUser,
  hasEmailSession,
  signOutEmail,
  type EmailAuthUser,
} from './emailAuth';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  emailVerified: boolean;
}

interface AuthContextType {
  user: User | null;
  authUser: AuthUser | null;
  loading: boolean;
  /**
   * Primary sign-in: routes to the in-app email-OTP screen (`/signup`),
   * preserving the target path so the flow can return there afterwards.
   */
  signIn: (from?: string) => Promise<void>;
  /**
   * Federated sign-in via Zitadel's hosted login (Google/Microsoft/Apple).
   * Still a redirect — social providers can't be done in-app.
   */
  signInWithOidc: (from?: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-read the email-OTP session (call after a successful in-app login). */
  refreshAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function deriveOidcAuthUser(user: User | null): AuthUser | null {
  if (!user) return null;
  const profile = user.profile;
  return {
    id: profile.sub,
    email: (profile.email as string | undefined) ?? '',
    fullName: (profile.name as string | undefined) ?? (profile.email as string | undefined) ?? '',
    emailVerified: (profile.email_verified as boolean | undefined) ?? false,
  };
}

/**
 * Customer-facing auth provider. Two session sources coexist:
 *   1. Email-OTP (primary) — tokens in localStorage, driven by the in-app
 *      `/signup` flow. This is the mobile-consistent path.
 *   2. OIDC (fallback) — Zitadel's hosted login, kept for federated social
 *      sign-in and any existing OIDC sessions.
 *
 * `apiClient` pulls the access token via a callback that prefers the email
 * session and falls back to OIDC. `authUser` is a flat projection of whichever
 * session is active.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [emailUser, setEmailUser] = useState<EmailAuthUser | null>(() =>
    hasEmailSession() ? getStoredUser() : null,
  );
  // If an email session already exists we don't block the UI on OIDC bootstrap.
  const [loading, setLoading] = useState(() => !hasEmailSession());

  const refreshAuth = useCallback(() => {
    setEmailUser(hasEmailSession() ? getStoredUser() : null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | undefined;

    // Token provider: email session first, OIDC second. Configured eagerly so
    // any early apiClient call resolves the right token.
    apiClient.setTokenProvider(async () => {
      const emailToken = await getEmailAccessToken();
      if (emailToken) return emailToken;
      try {
        const mgr = await getOidcUserManager();
        const u = await mgr.getUser();
        return u && !u.expired ? u.access_token : null;
      } catch {
        return null;
      }
    });

    getOidcUserManager()
      .then((mgr) => {
        if (cancelled) return;
        mgr.getUser().then((u) => {
          if (cancelled) return;
          setUser(u && !u.expired ? u : null);
          setLoading(false);
        });

        const onUserLoaded = (u: User) => setUser(u);
        const onUserUnloaded = () => setUser(null);
        const onSilentRenewError = (e: Error) => {
          console.warn('[Auth] silent renew failed', e);
        };

        mgr.events.addUserLoaded(onUserLoaded);
        mgr.events.addUserUnloaded(onUserUnloaded);
        mgr.events.addSilentRenewError(onSilentRenewError);

        cleanup = () => {
          mgr.events.removeUserLoaded(onUserLoaded);
          mgr.events.removeUserUnloaded(onUserUnloaded);
          mgr.events.removeSilentRenewError(onSilentRenewError);
        };
      })
      .catch((err) => {
        // Backend not yet reachable / config endpoint empty. The email-OTP path
        // doesn't need OIDC, so just unblock the UI; social sign-in retries.
        console.error('[Auth] OIDC bootstrap failed', err);
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  const signIn = useCallback(
    async (from?: string) => {
      navigate('/signup', from ? { state: { from } } : undefined);
    },
    [navigate],
  );

  const signInWithOidc = useCallback(async (from?: string) => {
    const mgr = await getOidcUserManager();
    await mgr.signinRedirect(from ? { state: { from } } : undefined);
  }, []);

  const signOut = useCallback(async () => {
    const hadEmailSession = hasEmailSession();
    signOutEmail();
    setEmailUser(null);

    if (hadEmailSession) {
      // Email sessions have no Zitadel browser session to end — just drop the
      // tokens and return to the sign-in screen.
      setUser(null);
      navigate('/signup', { replace: true });
      return;
    }

    try {
      const mgr = await getOidcUserManager();
      await mgr.signoutRedirect();
    } catch (err) {
      console.warn('[Auth] OIDC sign-out failed', err);
    }
    setUser(null);
  }, [navigate]);

  // An active email-OTP session was established by entering a code we sent to
  // the user's address — the email is verified by construction, so the
  // "verify your email" banner never applies. (OIDC/social sessions keep the
  // IdP's email_verified claim, which may legitimately be false.)
  const authUser: AuthUser | null = emailUser
    ? { ...emailUser, emailVerified: true }
    : deriveOidcAuthUser(user);

  return (
    <AuthContext.Provider
      value={{ user, authUser, loading, signIn, signInWithOidc, signOut, refreshAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
